"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TimeSeriesInteractiveLayer = void 0;
var React = require("react");
var memoize_one_1 = require("memoize-one");
var metrics_engine_1 = require("@tableau/metrics-engine");
var HtmlChiaSeed_1 = require("./HtmlChiaSeed");
/**
 * The `TimeSeriesInteractiveLayer` is responsible for rendering the elements of
 * the `DetailTimeSeries` that respond to user interaction, as well as tracking
 * pointer interactions with the time series.
 *
 * Specifically:
 * - The "Indicator Dot", also known as the {@link HtmlChiaSeed}, that is
 * displayed for the selected mark on the primary and comparison lines.
 * - The "Indicator Bar", which highlights the currently selected date/time
 * - The tooltip, which is displayed above the indicator bar while the user is
 * hovering over the timeseries.
 *
 * The key constraint for this class is that rendering remain _very fast_. We
 * want user interaction with the timeseries to feel instantanous, regardless of
 * how large the data table being rendered is.  As such, we need to limit the
 * amount of logic or computation that occurs as part of each render loop.
 * Rendering the entire `DetailTimeSeries` is too slow to do on mouse
 * interaction, so these interactive elements are kept seperate from the rest of
 * the time series chart.
 *
 * Currently, this class is an "uncontrolled component": it is responsible for
 * keeping track of the "selected data point", rather than being told which data
 * point is selected via the props interface. This means it is possible for the
 * BAN on the `MetricDetailView` to get out of sync with the selected data point
 * here. This is a potential hazard that we should remove, see [Story 1387382:
 * Use single source of truth for selected point in detail
 * screen](https://tfs.tsi.lan/tfs/DefaultCollection/Tableau-Dev/_workitems/edit/1387382)
 *
 * The current implementation tracks the selected mark in state via the
 * {@link TimeSeriesInteractiveLayerState.dataPointIndex}, and translates back and
 * forth between the index and the x/y coordinates of the selected point via
 * pre-calculated {@link HitMaps}.
 */
var TimeSeriesInteractiveLayer = /** @class */ (function (_super) {
    __extends(TimeSeriesInteractiveLayer, _super);
    function TimeSeriesInteractiveLayer(props) {
        var _this = _super.call(this, props) || this;
        _this.scrubInProgress = false;
        _this.handleMouseEnter = function () {
            _this.scrubInProgress = true;
        };
        _this.handleMouseLeave = function () {
            _this.scrubInProgress = false;
            _this.setState({ tooltipText: undefined });
        };
        _this.handleMouseMove = function (e) {
            _this.scrubInProgress = true;
            _this.updateForPointerLocation(e.currentTarget, e.clientX);
        };
        _this.handleTouchMove = function (e) {
            _this.updateForPointerLocation(e.currentTarget, e.touches.item(0).clientX);
        };
        _this.updateForPointerLocation = function (target, clientX) {
            var rect = target.getBoundingClientRect();
            var xOffset = rect.left;
            var x = Math.round(clientX - xOffset);
            var dataPointIndex = _this.hitMaps.mapOfPixelXCoordsToIndexInDataPoints[x];
            _this.updateDotAndLineLocationForDataPointIndex(dataPointIndex);
        };
        _this.updateDotAndLineLocationForDataPointIndex = function (dataPointIndex) {
            var dataPoint = _this.props.data[dataPointIndex];
            if (dataPoint) {
                _this.props.onDataPointActivated(dataPoint);
                var timestamp = _this.props.timestampAccessor && _this.props.timestampAccessor(dataPoint);
                var tooltipText = _this.scrubInProgress && timestamp ? _this.props.tooltipFormatter(timestamp) : undefined;
                _this.setState({ dataPointIndex: dataPointIndex, tooltipText: tooltipText });
            }
        };
        /**
         * This takes the size of the space the container View occupies,
         * then subtracts padding giving the data area size.  Then it builds the two
         * hit maps used during gesture handling.
         */
        _this.generateHitMaps = function () {
            return _this.generateHitMapsMemoized(_this.props.size, _this.props.padding, _this.props.domain, _this.props.data);
        };
        // tslint:disable-next-line: typedef
        _this.generateHitMapsMemoized = (0, memoize_one_1.default)(function (size, padding, domain, data) {
            var finalWidth = size.width - (padding.left + padding.right);
            var finalHeight = size.height - (padding.top + padding.bottom);
            return (0, metrics_engine_1.calculateHitMaps)(data, finalWidth, finalHeight, domain.x, domain.y, padding.right, _this.props.measureAccessor, _this.props.timestampAccessor && (0, metrics_engine_1.throwIfUndefined)(_this.props.timestampAccessor));
        });
        _this.hitMaps = _this.generateHitMaps();
        _this.state = { dataPointIndex: _this.props.data.length - 1 };
        return _this;
    }
    TimeSeriesInteractiveLayer.prototype.render = function () {
        var actualArea = {
            top: this.props.padding.top,
            left: this.props.padding.left,
            height: this.props.size.height - (this.props.padding.top + this.props.padding.bottom),
            width: this.props.size.width - this.props.padding.left,
        };
        this.hitMaps = this.generateHitMaps();
        var dataPointIndex = this.state.dataPointIndex < this.hitMaps.mapOfDataPointIndexToCoords.length
            ? this.state.dataPointIndex
            : this.hitMaps.mapOfDataPointIndexToCoords.length - 1;
        var _a = this.hitMaps.mapOfDataPointIndexToCoords[dataPointIndex], x = _a.x, y = _a.y;
        var dataRow = this.props.data[dataPointIndex];
        var comparisonMeasure = dataRow && this.props.comparisonAccessor && this.props.comparisonAccessor(dataRow);
        var comparisonY = comparisonMeasure && this.hitMaps.measureScale(comparisonMeasure);
        var statusColor = dataRow && this.props.colorAccessor(dataRow);
        var indicatorLineColor = this.props.colorIndicatorsEnabled ? metrics_engine_1.MetricsGrey : metrics_engine_1.MetricBlue;
        var dotColor = this.props.colorIndicatorsEnabled ? statusColor !== null && statusColor !== void 0 ? statusColor : metrics_engine_1.MetricsGrey : metrics_engine_1.MetricBlue;
        return (React.createElement("div", { "data-tb-test-id": "time-series-interactive-layer", style: __assign({ position: 'absolute' }, actualArea), onMouseMove: this.handleMouseMove, onMouseEnter: this.handleMouseEnter, onMouseLeave: this.handleMouseLeave, onTouchStart: this.handleMouseEnter, onTouchEnd: this.handleMouseLeave, onTouchMove: this.handleTouchMove },
            React.createElement("div", { style: __assign({ position: 'relative', height: actualArea.height + TOOLTIP_OFFSET, top: -TOOLTIP_OFFSET, transform: "translateX(" + (x - 1) + "px)" }, indicatorLineStyle(indicatorLineColor)) }),
            React.createElement("div", { style: __assign(__assign({ position: 'relative', transform: "translateX(" + x + "px)", top: -actualArea.top }, tooltipStyle), { color: indicatorLineColor }) }, this.state.tooltipText),
            this.props.shouldRenderComparisonDot && comparisonY !== undefined && (React.createElement(HtmlChiaSeed_1.HtmlChiaSeed, { x: x, y: comparisonY, color: metrics_engine_1.MetricComparisonColor, colorIndicatorsEnabled: this.props.colorIndicatorsEnabled, haloRatio: 0 })),
            y !== undefined && (React.createElement(HtmlChiaSeed_1.HtmlChiaSeed, { x: x, y: y, color: dotColor, colorIndicatorsEnabled: this.props.colorIndicatorsEnabled, haloRatio: 2.5 }))));
    };
    return TimeSeriesInteractiveLayer;
}(React.PureComponent));
exports.TimeSeriesInteractiveLayer = TimeSeriesInteractiveLayer;
var VERTICAL_BAR_TOOLTIP_CONTAINER_WIDTH = 160;
// We set both the background color and the left border style / color to support browser zoom.
// Background of 1px will allow the width to grow when the user zooms in.
// Left border width of 'thin' will allow the line to remain visible when the user zooms out.
var indicatorLineStyle = function (color) { return ({
    width: 1,
    borderLeftStyle: 'solid',
    borderLeftWidth: 'thin',
    borderLeftColor: color,
    backgroundColor: color,
    pointerEvents: 'none',
}); };
var TOOLTIP_OFFSET = 20;
var tooltipStyle = {
    position: 'absolute',
    left: -VERTICAL_BAR_TOOLTIP_CONTAINER_WIDTH / 2,
    width: VERTICAL_BAR_TOOLTIP_CONTAINER_WIDTH,
    marginTop: 10,
    textAlign: 'center',
    fontSize: 14,
    fontFamily: 'Benton Sans',
};
//# sourceMappingURL=TimeSeriesInteractiveLayer.js.map